import { emptyFuturesConfiguration, type FuturesConfigurationDraft } from '@/features/strategyBacktests/futuresConfigurationDraft';
import dayjs from 'dayjs';
import type { ScreenerUniverseEntityType } from '@/components/Screeners/ScreenerUniverseSelector';
import type { EntityConfig } from '@/components/UI/EntitySelectionPanel';
import type { SessionTimingPolicy } from '@einhaus/project314-types';
import type { IdeaLabRunDetail } from '../ideaLabUtils';

export type UniverseMode = 'entities' | 'screener';
export type SessionTimingMode = 'recommended' | 'nearClose' | 'atClosePlus1s' | 'nextOpen';

export interface DateTimeParts {
    date: string;
    time: string;
}

/** Every input of the New run form. */
export interface RunFormState {
    label: string;
    selectedVariantIds: number[];
    universeMode: UniverseMode;
    selectedEntities: EntityConfig[];
    entityListId: number | null;
    screenerRuleId: number | null;
    screenerEntityTypes: ScreenerUniverseEntityType[];
    start: DateTimeParts;
    end: DateTimeParts;
    useWalkForward: boolean;
    walkForwardStart: DateTimeParts;
    /** Null selects the duration's whole horizon menu. */
    selectedHorizons: number[] | null;
    afterHoursRemoved: boolean;
    sessionTimingMode: SessionTimingMode;
    preCloseOffsetSecs: number | null;
    includeUnderlying: boolean;
    includeFutures: boolean;
    futuresDrafts: Record<number, FuturesConfigurationDraft>;
    includeOptions: boolean;
    optionBuyingStrategyId: number | null;
    optionType: 'call' | 'put' | null;
}

export const DEFAULT_PRE_CLOSE_OFFSET_SECS = 120;

export const splitDateTime = (value: string): DateTimeParts => {
    const parsed = dayjs(value);

    return { date: parsed.format('YYYY-MM-DD'), time: parsed.format('HH:mm:ss') };
};

export const joinDateTime = (parts: DateTimeParts): string => `${parts.date} ${parts.time.length === 5 ? `${parts.time}:00` : parts.time}`;

/** The session policy each explicit timing mode submits for a duration. */
export const buildSessionTimingModePolicy = (
    mode: Exclude<SessionTimingMode, 'recommended'>,
    preCloseOffsetSecs: number | null
): SessionTimingPolicy =>
    mode === 'nearClose'
        ? {
              detectionCutoff: 'nearClose',
              executionPolicy: 'nearClose',
              preCloseOffsetSecs: preCloseOffsetSecs ?? DEFAULT_PRE_CLOSE_OFFSET_SECS
          }
        : { detectionCutoff: 'close', executionPolicy: mode };

/**
 * The form mode that reproduces a frozen policy exactly. A policy no explicit mode can express
 * falls back to the recommended mode, whose resolution the workload preview shows before queueing.
 */
export const sessionTimingModeFromPolicy = (
    policy: SessionTimingPolicy | undefined
): { mode: SessionTimingMode; preCloseOffsetSecs: number | null } => {
    if (policy === undefined) return { mode: 'recommended', preCloseOffsetSecs: DEFAULT_PRE_CLOSE_OFFSET_SECS };

    const mode = policy.executionPolicy;
    const offset = policy.preCloseOffsetSecs ?? null;
    const expected = buildSessionTimingModePolicy(mode, offset);
    const reproducible =
        expected.detectionCutoff === policy.detectionCutoff &&
        (mode !== 'nearClose' || (offset !== null && offset === expected.preCloseOffsetSecs));

    return reproducible
        ? { mode, preCloseOffsetSecs: mode === 'nearClose' ? offset : DEFAULT_PRE_CLOSE_OFFSET_SECS }
        : { mode: 'recommended', preCloseOffsetSecs: DEFAULT_PRE_CLOSE_OFFSET_SECS };
};

export const buildDefaultRunFormState = (defaultWindow: {
    trainingStart: string;
    walkForwardStart: string;
    walkForwardEnd: string;
}): RunFormState => ({
    label: '',
    selectedVariantIds: [],
    universeMode: 'entities',
    selectedEntities: [],
    entityListId: null,
    screenerRuleId: null,
    screenerEntityTypes: [],
    start: splitDateTime(defaultWindow.trainingStart),
    end: splitDateTime(defaultWindow.walkForwardEnd),
    useWalkForward: true,
    walkForwardStart: splitDateTime(defaultWindow.walkForwardStart),
    selectedHorizons: null,
    afterHoursRemoved: true,
    sessionTimingMode: 'recommended',
    preCloseOffsetSecs: DEFAULT_PRE_CLOSE_OFFSET_SECS,
    includeUnderlying: true,
    includeFutures: false,
    futuresDrafts: {},
    includeOptions: false,
    optionBuyingStrategyId: null,
    optionType: null
});

export interface RunFormPrefill {
    state: RunFormState;
    /** Variants of the source run that can no longer join a run (archived) and were left out. */
    droppedVariantCount: number;
}

/**
 * Pre-fills the form with a run's frozen settings so a follow-up run changes only what the user
 * edits. The universe is the run's resolved symbol list (with its source entity list when it had
 * one), and variants that can no longer run are dropped.
 */
export const buildRunFormStateFromRun = (
    run: IdeaLabRunDetail,
    runnableVariantIds: ReadonlySet<number>,
    defaults: RunFormState
): RunFormPrefill => {
    const sourceVariantIds = run.variants.map((variant) => variant.variantId);
    const selectedVariantIds = sourceVariantIds.filter((variantId) => runnableVariantIds.has(variantId));
    const timing = sessionTimingModeFromPolicy(run.sessionTimingPolicy?.[run.primaryDuration]);

    return {
        droppedVariantCount: sourceVariantIds.length - selectedVariantIds.length,
        state: {
            ...defaults,
            label: `${run.label} (copy)`,
            selectedVariantIds,
            universeMode: 'entities',
            selectedEntities: run.entities.map((entity) => ({
                entityType: entity.entityType,
                entityId: entity.entityId,
                symbol: entity.symbol
            })),
            entityListId: run.entityListId,
            screenerRuleId: null,
            screenerEntityTypes: [],
            start: splitDateTime(run.startDateTime),
            end: splitDateTime(run.endDateTime),
            useWalkForward: run.walkForwardStartDateTime !== null,
            walkForwardStart:
                run.walkForwardStartDateTime === null ? defaults.walkForwardStart : splitDateTime(run.walkForwardStartDateTime),
            selectedHorizons: [...new Set(run.horizons.map((horizon) => horizon.horizonSeconds))].sort((left, right) => left - right),
            afterHoursRemoved: run.afterHoursRemoved,
            sessionTimingMode: timing.mode,
            preCloseOffsetSecs: timing.preCloseOffsetSecs,
            includeUnderlying: run.includeUnderlying,
            includeFutures: run.futures.length > 0,
            futuresDrafts: Object.fromEntries(
                run.futuresSelections.map((selection) => {
                    const raw = BigInt(selection.execution.allocationMoneyRaw);
                    const fraction = (raw % 1_000_000_000n).toString().padStart(9, '0').replace(/0+$/, '');
                    return [
                        selection.signalAssetId,
                        {
                            ...emptyFuturesConfiguration(),
                            productId: selection.execution.productId,
                            assumptionId: selection.execution.assumptionId,
                            historyStart: selection.execution.historyStartDateTime,
                            allocation: `${raw / 1_000_000_000n}${fraction ? `.${fraction}` : ''}`,
                            closeBufferMinutes: String(selection.execution.closeBufferSeconds / 60)
                        }
                    ];
                })
            ),
            includeOptions: run.optionLens !== null,
            optionBuyingStrategyId: run.optionLens?.optionBuyingStrategyId ?? null,
            optionType: run.optionLens?.fixedOptionType === 'c' ? 'call' : run.optionLens?.fixedOptionType === 'p' ? 'put' : null
        }
    };
};
